import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, PaddingSize } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import { UseComboboxStateChange } from 'downshift';
import { Stack, TextFormField } from '../../ui';
import { AutocompleteFormField, SelectFormField } from '../../ui/FormFields';
import { useAsyncState } from '../../../hooks';
import getAutocompleteLocations, { getFullAddress } from '../../../modules/maps/locationAutocomplete';
import { US_STATE_ABBREVIATIONS } from '../../../modules/stringUtils';
import { Z_FG } from '../../ui/utilities/zIndex';
import { isValidStreetAddress, isValidZipcode } from '../../../modules/validators';

const Item = ({ children }) => (
  <Box paddingY={8} paddingX={16}>
    {children}
  </Box>
);

export type AddressServerErrors = {
  location_address1?: string;
  location_city?: string;
  location_state?: string;
  location_zipcode?: string;
};

export type Address = {
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
};

type AutocompleteAddressFormProps = Address & {
  space?: PaddingSize;
  onChange: (address: Address) => void;
  onValidationChange?: (isValid: boolean) => void;
  errors?: Partial<AddressServerErrors>;
  disabled?: boolean;
  isRequired?: boolean;
};

const isValidAutoCompleteZipCode = (
  zipcode: string,
) => zipcode.length === 5 && isValidZipcode(zipcode);

export const AutocompleteAddressForm: React.FC<AutocompleteAddressFormProps> = ({
  space = 16,
  address: initialAddress,
  address2: initialAddress2,
  city: initialCity,
  state: initialState,
  zipcode: initialZipcode,
  errors,
  disabled,
  onChange,
  onValidationChange,
  isRequired,
}) => {
  const googleMapRef = useRef(null);
  const [addressParts, setAddressParts] = useState<Address>({
    address: initialAddress || '',
    address2: initialAddress2 || '',
    city: initialCity || '',
    state: initialState || '',
    zipcode: initialZipcode || '',
  });
  const [
    isStreetAddressValid,
    setIsStreetAddressValid,
  ] = useState<boolean>(!errors?.location_address1);
  const [isCityValid, setIsCityValid] = useState<boolean>(!errors?.location_city);
  const [isStateValid, setIsStateValid] = useState<boolean>(!errors?.location_state);
  const [isZipcodeValid, setIsZipcodeValid] = useState<boolean>(!errors?.location_zipcode);
  const isValid = useMemo(() => (
    isStreetAddressValid
    && isCityValid
    && isStateValid
    && isZipcodeValid
  ), [
    isStreetAddressValid,
    isCityValid,
    isStateValid,
    isZipcodeValid,
  ]);
  const places = useAsyncState(
    async () => {
      const results = await getAutocompleteLocations(addressParts?.address);
      return results || [];
    },
    [addressParts?.address],
  );
  const locations = useMemo(() => (places || []).map(loc => loc.description), [places]);

  const handleAddressPartsChange = useCallback((addr: Partial<Address>) => {
    if (addr?.city) {
      setIsCityValid(true);
    }
    if (!addr?.address || !!addr?.state) {
      setIsStateValid(true);
    }
    if (addr?.zipcode) {
      setIsZipcodeValid(true);
    }
    if (!isRequired || (addr?.address && isValidStreetAddress(addr?.address))) {
      setIsStreetAddressValid(true);
    }
    setAddressParts(prevAddress => {
      onChange?.({
        ...prevAddress,
        ...addr,
      });
      return ({
        ...prevAddress,
        ...addr,
      });
    });
  }, [
    onChange,
    isRequired,
  ]);

  const handleLocationSelection = async (
    { selectedItem }: UseComboboxStateChange<string>,
  ) => {
    const [
      newAddress,
      newCity,
      newState,
    ] = selectedItem.split(', ');
    const place = places.find(loc => loc.description === selectedItem) || {};

    const { location_zipcode } = await getFullAddress(
      place?.place_id,
      googleMapRef.current,
      newAddress,
    );
    handleAddressPartsChange({
      address: newAddress,
      address2: '',
      city: newCity,
      state: newState,
      zipcode: location_zipcode,
    });
  };

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [
    isValid,
    onValidationChange,
  ]);

  /**
   * Note that this validation only handles forms where the address isn't
   * required, but if an address line is provided then city,state,and zip are required
   * Validation will need to be extended more to handle other adress use cases
   */
  const handleValidation = () => {
    if (!addressParts?.address) {
      if (isRequired) {
        setIsStreetAddressValid(false);
      }
      setIsCityValid(true);
      setIsStateValid(true);
      setIsZipcodeValid(
        !addressParts?.zipcode
        || isValidAutoCompleteZipCode(addressParts?.zipcode),
      );
    } else {
      setIsStreetAddressValid(isValidStreetAddress(addressParts?.address));
      setIsCityValid(!!addressParts?.city);
      setIsStateValid(!!addressParts?.state);
      setIsZipcodeValid(isValidAutoCompleteZipCode(addressParts?.zipcode));
    }
  };

  const handleAddressChange = useCallback((newAddress: string) => {
    handleAddressPartsChange({ address: newAddress });
  }, [handleAddressPartsChange]);

  const handleAddress2Change = useCallback((newAddress: string) => {
    handleAddressPartsChange({ address2: newAddress });
  }, [handleAddressPartsChange]);

  const handleCityChange = useCallback((newCity: string) => {
    handleAddressPartsChange({ city: newCity });
  }, [handleAddressPartsChange]);

  const handleStateChange = useCallback((newState: string) => {
    handleAddressPartsChange({ state: newState });
  }, [handleAddressPartsChange]);

  const handleZipChange = useCallback((newZip: string) => {
    handleAddressPartsChange({ zipcode: newZip });
  }, [handleAddressPartsChange]);

  return (
    <React.Fragment>
      {/* TODO (Future) - Google maps needs an HTML element ref for the Maps API
        This will need to be updated for React Native
      */}
      <div ref={googleMapRef} />
      <Stack
        space={space}
      >
        <Box zIndex={Z_FG}>
          <AutocompleteFormField
            placeholder="e.g. 111 South Ave"
            value={addressParts?.address}
            onChangeText={handleAddressChange}
            type="text"
            label="Street Address"
            Item={Item}
            items={locations}
            handleSelectedItemChange={handleLocationSelection}
            onBlur={handleValidation}
            accentColor={!isStreetAddressValid ? colors.red : undefined}
            message={!isStreetAddressValid ? 'Please enter a valid address' : undefined}
            disabled={disabled}
            testID="autocomplete-address-line1"
          />
        </Box>
        <TextFormField
          label="Suite, Apt, etc. (optional)"
          placeholder="e.g. Suite 201"
          value={addressParts?.address2}
          onChangeText={handleAddress2Change}
          type="text"
          disabled={disabled}
          testID="autocomplete-address-line2"
        />
        <TextFormField
          placeholder="e.g. San Francisco"
          value={addressParts?.city}
          onChangeText={handleCityChange}
          type="text"
          label="City"
          onBlur={handleValidation}
          accentColor={isCityValid ? undefined : colors.red}
          message={isCityValid ? undefined : 'Please enter a city'}
          disabled={disabled}
          testID="autocomplete-address-city"
        />
        <Box zIndex={Z_FG}>
          <SelectFormField
            label="State"
            items={US_STATE_ABBREVIATIONS}
            value={addressParts?.state}
            placeholder="e.g. CA"
            onChange={handleStateChange}
            accentColor={isStateValid ? undefined : colors.red}
            message={isStateValid ? undefined : 'Please enter a state'}
            disabled={disabled}
            testID="autocomplete-address-state"
          />
        </Box>
        <TextFormField
          label="Zip"
          placeholder="e.g. 94104"
          value={addressParts?.zipcode}
          onChangeText={handleZipChange}
          onBlur={handleValidation}
          type="text"
          accentColor={isZipcodeValid ? undefined : colors.red}
          message={isZipcodeValid ? undefined : 'Please enter a valid zip code'}
          disabled={disabled}
          testID="autocomplete-address-zipcode"
        />
      </Stack>
    </React.Fragment>
  );
};
